import { ICustomReactSelectOption } from "core/types/elements/ICustomReactSelectOption";
import React, { FC, useState } from "react";
import Select, { ActionMeta, StylesConfig } from "react-select";

import "./CustomReactSelect.scss";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";

type TProps = {
  inputId?: string;
  options: ICustomReactSelectOption[];
  onChange: ((newValue: any, actionMeta: ActionMeta<any>) => void) | undefined;
  placeholder?: string;
  className?: string;
  value?: any;
  defaultValue?: ICustomReactSelectOption;
  isMulti?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  isDisabled?: boolean;
  isOptionDisabled?: (() => boolean) | undefined;
  async?: boolean;
  styles?: StylesConfig<any, boolean>;
  menuPortalTarget?: HTMLElement | null;
  formatOptionLabel?: (data: any, meta: { context: "menu" | "value" }) => React.ReactNode;
  creatable?: boolean;
};

const selectStyles = {
  control: (base: any) => ({
    ...base,
    border: 0,
    boxShadow: "none",
  }),
  valueContainer: (base: any, data: any) => {
    return data?.options.length && data?.options[0].selectData === "limitedHeight"
      ? {
          ...base,
          maxHeight: "46px",
        }
      : base;
  },
  option: (styles: any, { data }: any) => {
    return data.selectDisabled
      ? {
          ...styles,
          color: data.selectDisabled ? "hsl(0,1%,73%)" : "default",
        }
      : styles;
  },
};

const CustomReactSelector: FC<TProps> = ({
  inputId,
  options,
  onChange,
  placeholder,
  defaultValue,
  value,
  className,
  isClearable,
  isMulti,
  closeMenuOnSelect,
  hideSelectedOptions,
  isDisabled,
  async,
  isOptionDisabled,
  styles,
  menuPortalTarget,
  formatOptionLabel,
  creatable,
}) => {
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>();
  const filterOptions = (inputValue: string) => {
    return options.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
  };

  const loadOptions = (
    inputValue: string,
    callback: (options: ICustomReactSelectOption[]) => void,
  ) => {
    clearTimeout(timer);

    const newTimer = setTimeout(() => {
      callback(filterOptions(inputValue));
    }, 500);

    setTimer(newTimer);
  };

  if (async) {
    return (
      <AsyncSelect
        cacheOptions
        inputId={inputId}
        options={options}
        className={className || ""}
        loadOptions={loadOptions}
        classNamePrefix="custom-react-select"
        placeholder={placeholder || "Select Item"}
        onChange={onChange}
        isMulti={isMulti || false}
        isClearable={isClearable || false}
        value={value || null}
        defaultValue={defaultValue || null}
        isDisabled={isDisabled}
        closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : true}
        hideSelectedOptions={hideSelectedOptions === undefined}
        escapeClearsValue
        styles={styles || selectStyles}
        defaultOptions
        menuPortalTarget={menuPortalTarget}
        formatOptionLabel={formatOptionLabel}
      />
    );
  }

  if (creatable) {
    return (
      <CreatableSelect
        className={className || ""}
        inputId={inputId}
        options={options}
        classNamePrefix="custom-react-select"
        placeholder={placeholder || "Select Item"}
        onChange={onChange}
        isMulti={isMulti || false}
        isClearable={isClearable || false}
        value={value || null}
        defaultValue={defaultValue || null}
        isDisabled={isDisabled}
        closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : true}
        hideSelectedOptions={hideSelectedOptions === undefined}
        escapeClearsValue
        styles={styles || selectStyles}
        isOptionDisabled={isOptionDisabled}
        isOptionSelected={(option, selectValue) =>
          selectValue.some((i: any) => i.value === option.value)
        }
        menuPortalTarget={menuPortalTarget}
        formatOptionLabel={formatOptionLabel}
      />
    );
  }

  return (
    <Select
      className={className || ""}
      inputId={inputId}
      options={options}
      classNamePrefix="custom-react-select"
      placeholder={placeholder || "Select Item"}
      onChange={onChange}
      isMulti={isMulti || false}
      isClearable={isClearable || false}
      value={value || null}
      defaultValue={defaultValue || null}
      isDisabled={isDisabled}
      closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : true}
      hideSelectedOptions={hideSelectedOptions === undefined}
      escapeClearsValue
      styles={styles || selectStyles}
      isOptionDisabled={isOptionDisabled}
      // Fixed options with same values, like few options with same name: Alex Smith and Alex Smith
      isOptionSelected={(option, selectValue) => selectValue.some((i) => i === option)}
      menuPortalTarget={menuPortalTarget}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default CustomReactSelector;
